import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Code, ArrowLeft, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';

const StudentCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [progress, setProgress] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [hasReviewed, setHasReviewed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'student') {
      navigate('/login');
      return;
    }
    fetchData();
  }, [courseId, user, navigate]);

  const fetchData = async () => {
    try {
      const [courseRes, enrollmentsRes, reviewsRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get('/enrollments'),
        api.get(`/courses/${courseId}/reviews`)
      ]);
      setCourse(courseRes.data);
      const currentEnrollment = enrollmentsRes.data.find(e => e.course_id === courseId);
      setEnrollment(currentEnrollment);
      setProgress(currentEnrollment?.progress || 0);
      setReviews(reviewsRes.data);
      setHasReviewed(reviewsRes.data.some(r => r.student_id === user.id));
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleProgressUpdate = async () => {
    try {
      await api.patch(`/enrollments/${enrollment.id}/progress`, { progress });
      toast.success('Progress updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/courses/${courseId}/reviews`, reviewForm);
      toast.success('Review submitted successfully!');
      setReviewForm({ rating: 5, comment: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!course || !enrollment) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Code className="w-6 h-6 text-violet-600" />
              <span className="text-lg font-bold text-slate-900">CodeITT</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-4xl">
        {/* Course Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-6" data-testid="course-header">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium text-white mb-4 ${
            course.level === 'beginner' ? 'bg-emerald-600' :
            course.level === 'intermediate' ? 'bg-amber-600' :
            'bg-red-600'
          }`}>
            {course.level.toUpperCase()}
          </span>
          <h1 className="text-4xl font-bold text-slate-900 mb-3">{course.title}</h1>
          <p className="text-lg text-slate-600 mb-4">{course.description}</p>
          <p className="text-sm text-slate-500">Taught by {course.teacher_name}</p>
        </div>

        {/* Progress Card */}
        <div className="bg-gradient-to-br from-violet-600 to-indigo-600 rounded-xl p-8 mb-6 text-white" data-testid="progress-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Your Progress</h2>
            <div className="text-4xl font-bold">{enrollment.progress}%</div>
          </div>
          <Progress value={enrollment.progress} className="h-3 bg-white/20 mb-6" />
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mt-4">
            <Label className="text-white mb-3 block font-medium">Update your progress:</Label>
            <div className="flex items-center space-x-4">
              <Slider
                value={[progress]}
                onValueChange={(value) => setProgress(value[0])}
                max={100}
                step={5}
                className="flex-1"
                data-testid="progress-slider"
              />
              <span className="text-2xl font-bold w-16">{progress}%</span>
            </div>
            <Button
              onClick={handleProgressUpdate}
              className="w-full mt-4 bg-white text-violet-600 hover:bg-slate-50 font-medium"
              data-testid="update-progress-btn"
            >
              Update Progress
            </Button>
          </div>
        </div>

        {/* Course Content */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6" data-testid="course-content">
          <div className="bg-slate-900 px-6 py-4 flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-bold text-white">Course Content</h2>
          </div>
          <div className="p-8">
            <div className="code-editor">
              <pre>{course.content}</pre>
            </div>
          </div>
        </div>

        {/* Video Section */}
        {course.video_url && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6" data-testid="course-video">
            <div className="bg-slate-900 px-6 py-4">
              <h2 className="text-lg font-bold text-white">📹 Course Video</h2>
            </div>
            <div className="p-4">
              <div className="aspect-video">
                <iframe
                  src={course.video_url}
                  className="w-full h-full rounded-lg"
                  allowFullScreen
                  title="Course Video"
                />
              </div>
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="reviews-section">
          <div className="bg-slate-900 px-6 py-4">
            <h2 className="text-lg font-bold text-white">⭐ Reviews ({reviews.length})</h2>
          </div>
          <div className="p-8">
            {/* Submit Review Form */}
            {!hasReviewed && (
              <form onSubmit={handleReviewSubmit} className="mb-8 p-6 bg-violet-50 rounded-lg" data-testid="review-form">
                <h3 className="font-semibold text-slate-900 mb-4">Write a Review</h3>
                <div className="mb-4">
                  <Label>Rating</Label>
                  <div className="flex gap-2 mt-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({...reviewForm, rating: star})}
                        className={`text-3xl ${star <= reviewForm.rating ? 'text-yellow-500' : 'text-slate-300'}`}
                        data-testid={`star-${star}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <Label htmlFor="comment">Your Review</Label>
                  <Textarea
                    id="comment"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                    required
                    rows={4}
                    className="mt-1"
                    placeholder="Share your experience with this course..."
                    data-testid="review-comment-input"
                  />
                </div>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="submit-review-btn">
                  Submit Review
                </Button>
              </form>
            )}

            {/* Reviews List */}
            {reviews.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No reviews yet. Be the first to review!</p>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => (
                  <div key={review.id} className="p-4 bg-slate-50 rounded-lg" data-testid={`review-${review.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{review.student_name}</p>
                        <div className="flex items-center gap-1 text-yellow-500">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <span key={i}>★</span>
                          ))}
                        </div>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-slate-700">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Label = ({ children, className = '', htmlFor }) => (
  <label htmlFor={htmlFor} className={`text-sm font-medium ${className}`}>{children}</label>
);

const Textarea = ({ className = '', ...props }) => (
  <textarea
    className={`flex min-h-[80px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

export default StudentCoursePage;